/**
 * centralized state management for metadoc page
 * consolidates local state and provides actions for state updates
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { TabId } from "../../metadoc.types";

type UseMetadocStateArgs = {
  metadocId?: string;
};

type UseMetadocStateReturn = {
  // selection state
  selectedDocumentIds: Set<number>;
  setSelectedDocumentIds: (ids: Set<number>) => void;
  selectedPages: Set<number>;
  setSelectedPages: (pages: Set<number>) => void;
  clearSelection: () => void;

  // tab state
  activeTab: TabId;
  setActiveTab: (tab: TabId) => void;

  // page operation state
  isPageDeletionInProgress: boolean;
  setIsPageDeletionInProgress: (inProgress: boolean) => void;

  // metadoc tracking
  previousMetadocIdRef: React.MutableRefObject<string | undefined>;
};

export const useMetadocState = ({
  metadocId,
}: UseMetadocStateArgs): UseMetadocStateReturn => {
  // selection state for document picker
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<Set<number>>(
    new Set(),
  );

  // selection state for pages
  const [selectedPages, setSelectedPages] = useState<Set<number>>(new Set());

  // tab state
  const [activeTab, setActiveTab] = useState<TabId>("overview");

  // page operation state
  const [isPageDeletionInProgress, setIsPageDeletionInProgress] =
    useState(false);

  // track previous metadocId for reset logic
  const previousMetadocIdRef = useRef<string | undefined>(metadocId);

  // reset metadoc-scoped ui state when metadoc changes (switching between metadocs).
  useEffect(() => {
    const previousMetadocId = previousMetadocIdRef.current;
    const metadocIdChanged = previousMetadocId !== metadocId;

    if (metadocIdChanged) {
      setActiveTab("overview");
      setIsPageDeletionInProgress(false);
      previousMetadocIdRef.current = metadocId;
    }
  }, [metadocId]);

  const clearSelection = useCallback(() => {
    setSelectedPages(new Set());
  }, []);

  // memoize the return object to prevent infinite loops in useEffect dependencies
  return useMemo(
    () => ({
      selectedDocumentIds,
      setSelectedDocumentIds,
      selectedPages,
      setSelectedPages,
      clearSelection,
      activeTab,
      setActiveTab,
      isPageDeletionInProgress,
      setIsPageDeletionInProgress,
      previousMetadocIdRef,
    }),
    [
      selectedDocumentIds,
      selectedPages,
      clearSelection,
      activeTab,
      isPageDeletionInProgress,
    ],
  );
};
